import { Component, HostBinding, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, NavigationCancel, NavigationEnd } from '@angular/router';
import { MediaChange, ObservableMedia } from '@angular/flex-layout';
import { MatSelectChange } from '@angular/material';

import { Subject } from 'rxjs/Subject';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { distinctUntilChanged } from 'rxjs/operators/distinctUntilChanged';
import { filter } from 'rxjs/operators/filter';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { tap } from 'rxjs/operators/tap';
import { zip } from 'rxjs/observable/zip';

import { format, isValid } from 'date-fns';

import { ApplicationService, BasicStudentInfo, CollegeInfo } from '../services/application/application.service';
import { AuthService } from '../services/authentication/auth.service';
import { DialogService, ConfirmationDialogOptions } from '../services/dialogs/dialogs.service';
import { ErrorService } from '../services/error/error.service';
import { NavMenuItem } from './ui/navmenu/navmenu.component';
import { SplashService } from '../services/splash/splash.service';
import { UserInfo, UserService } from '../services/user/user.service';
import { BUILD_REASON, BUILD_TIME, VERSION_NUMBER } from '../services/version/version.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {
    @HostBinding('class.smalldisplay') SmallDisplay: boolean;

    private _subRelease: Subject<void> = new Subject<void>();
    AboutFn: Function;
    LogoutFn: Function;

    AdminCollegeID: number;
    AllSchoolYears: string[];
    AvailableColleges: CollegeInfo[];
    BuildReason: string;
    BuildTime: string;
    DevSite: boolean;
    LastSizeCode: string;
    MenuItems: NavMenuItem[];
    MenuOpen: boolean;
    SelectedSchoolYear: string;
    SelectedStudent: BasicStudentInfo;
    ShowCollegeFilter: boolean;
    SizeCode: string;
    SmallSize: boolean;
    UpdateAvailable: boolean;
    User: UserInfo;
    VersionLoaded: string;
    ViewReady: boolean;

    constructor(
        private appService: ApplicationService,
        private authService: AuthService,
        private dialogService: DialogService,
        private errorService: ErrorService,
        private media: ObservableMedia,
        private route: ActivatedRoute,
        private router: Router,
        private splashService: SplashService,
        private userService: UserService
    ) {
        const self = this;

        this.AboutFn = this.About.bind(this);
        this.AvailableColleges = null;
        this.BuildReason = BUILD_REASON;
        this.LogoutFn = this.Logout.bind(this);
        this.MenuOpen = false;
        this.SelectedSchoolYear = null;
        this.ShowCollegeFilter = true;
        this.SmallDisplay = false;
        this.UpdateAvailable = false;
        this.VersionLoaded = VERSION_NUMBER;
        this.ViewReady = false;

        const btime = new Date(BUILD_TIME);
        if (isValid(btime)) {
            this.BuildTime = format(btime, 'MMM. DD, YYYY h:mm a');
        }

        // Check for cancelled router navigation
        this.router.events
            .pipe(
                takeUntil(this._subRelease),
                filter((event) => event instanceof NavigationCancel)
            )
            .subscribe((ev) => {
                // Drop splash (if any), and show a snackbar alert.
                this.splashService.CloseSplash();
            });

        // Subscribe to checks for a version upgrade
        this.authService.HasVersionUpdate.pipe(takeUntil(this._subRelease)).subscribe((value) => {
            if (value === true) {
                // Set the upgrade alert banner.
                this.UpdateAvailable = true;
            }
        });

        // Subscribe to media changes
        this.media
            .asObservable()
            .pipe(takeUntil(this._subRelease))
            .subscribe((change: MediaChange) => {
                this.SmallSize = this.media.isActive('xs') || this.media.isActive('sm');
                this.SmallDisplay = this.SmallSize;
                this.SizeCode =
                    change.mqAlias === 'xl'
                        ? 'xl'
                        : change.mqAlias === 'lg'
                        ? 'lg'
                        : change.mqAlias === 'md'
                        ? 'md'
                        : change.mqAlias === 'sm'
                        ? 'sm'
                        : 'xs';

                if (this.SmallSize) {
                    this.MenuOpen = false;
                } else {
                    this.MenuOpen = true;
                }
            });

        // Check for studentview route
        this.router.events
            .pipe(
                takeUntil(this._subRelease),
                filter((event) => event instanceof NavigationEnd)
            )
            .subscribe((ev: NavigationEnd) => {
                this.ShowCollegeFilter = (ev.url.indexOf('studentview') >= 0) ? false : true
            });

        this.SmallSize = this.media.isActive('xs') || this.media.isActive('sm');
        this.SmallDisplay = this.SmallSize;
        this.SizeCode = this.media.isActive('xl')
            ? 'xl'
            : this.media.isActive('lg')
            ? 'lg'
            : this.media.isActive('md')
            ? 'md'
            : this.media.isActive('sm')
            ? 'sm'
            : 'xs';
        this.LastSizeCode = this.SizeCode;

        if (this.SmallSize) {
            this.MenuOpen = false;
        } else {
            this.MenuOpen = true;
        }

        // Subscribe to user information changes for the logged in user, along with selected student changes.
        combineLatest(
            this.userService.UserSubject.pipe(distinctUntilChanged()),
            this.appService.SelectedStudentSubject,
            this.appService.SelectedSchoolYearSubject,
            this.appService.ParentStudentChildrenSubject,
            this.appService.SelectedAdminCollegeIDSubject,
            function (user, student, selyear, parstus, colId) {
                return {
                    User: user,
                    Student: student,
                    SelectedYear: selyear,
                    ParentStudents: parstus,
                    SelectedAdminCollegeID: colId,
                };
            }
        )
            .pipe(takeUntil(this._subRelease))
            .subscribe((packet) => {
                this.User = packet.User;
                this.SelectedStudent = packet.Student;
                this.SelectedSchoolYear = packet.SelectedYear;
                this.AdminCollegeID = packet.SelectedAdminCollegeID;

                if (this.User) {
                    // [Re]set up the navmenu entries
                    const homeRoute = '/home';

                    const tempmenu: NavMenuItem[] = [
                        {
                            MenuText: 'Home',
                            MenuIcon: 'home',
                            AllowedRoles: ['all'],
                            LinkedStates: ['/home'],
                            IsHome: true,
                            ActionRoute: [homeRoute],
                        },
                        {
                            MenuText: 'Select Student',
                            MenuIcon: 'people',
                            AllowedRoles: ['parent'],
                            LinkedStates: ['/studentselect'],
                            ActionRoute: ['/studentselect'],
                            Exclude: !(packet.ParentStudents && packet.ParentStudents.length > 1),
                        },
                        {
                            MenuText: 'Course Requests',
                            MenuIcon: 'school',
                            AllowedRoles: ['student'],
                            LinkedStates: ['/studentview'],
                            ActionRoute: ['/studentview', this.User.StudentID],
                        },
                        {
                            MenuText: 'Course Requests',
                            MenuIcon: 'school',
                            AllowedRoles: ['parent'],
                            LinkedStates: ['/studentview'],
                            ActionRoute: ['/studentview', this.SelectedStudent && this.SelectedStudent.StudentID],
                            Exclude: !this.SelectedStudent,
                        },
                        {
                            MenuText: 'Contact Info',
                            MenuIcon: 'perm_device_info',
                            AllowedRoles: ['student'],
                            LinkedStates: ['/studentcontact'],
                            ActionRoute: ['/studentcontact'],
                        },
                        {
                            MenuText: 'Contact Info',
                            MenuIcon: 'perm_device_info',
                            AllowedRoles: ['parent'],
                            LinkedStates: ['/parentcontact'],
                            ActionRoute: ['/parentcontact'],
                        },
                        {
                            MenuText: 'View Student',
                            MenuIcon: 'person',
                            AllowedRoles: [
                                'counselor',
                                'schooladmin',
                                'advisor',
                                'collegeadmin',
                                'collegesupport',
                                'districtadmin',
                                'developer',
                            ],
                            LinkedStates: ['/studentview'],
                            ActionRoute: ['/studentview', this.SelectedStudent && this.SelectedStudent.StudentID],
                            Exclude: !this.SelectedStudent,
                        },
                        {
                            MenuText: 'Admin Settings',
                            MenuIcon: 'settings',
                            AllowedRoles: ['developer'],
                            LinkedStates: ['/adminsettings'],
                            ActionRoute: ['/adminsettings'],
                        },
                        {
                            MenuText: 'Adoption Requests',
                            MenuIcon: 'speaker_notes',
                            AllowedRoles: ['counselor', 'schooladmin'],
                            LinkedStates: ['/adoptionrequest'],
                            ActionRoute: ['/adoptionrequest'],
                        },
                        {
                            MenuText: 'Adoption Requests',
                            MenuIcon: 'speaker_notes',
                            AllowedRoles: ['districtadmin', 'developer'],
                            LinkedStates: ['/manageadoptions'],
                            ActionRoute: ['/manageadoptions'],
                        },
                        {
                            MenuText: 'Manage College Staff',
                            MenuIcon: 'supervisor_account',
                            AllowedRoles: ['collegeadmin', 'districtadmin', 'developer'],
                            LinkedStates: ['/managestaff'],
                            ActionRoute: ['/managestaff'],
                        },
                        {
                            MenuText: 'Manage Designees',
                            MenuIcon: 'account_circle',
                            AllowedRoles: ['districtadmin', 'developer'],
                            LinkedStates: ['/managedesignees'],
                            ActionRoute: ['/managedesignees'],
                        },
                        {
                            MenuText: 'Manage Terms',
                            MenuIcon: 'event',
                            AllowedRoles: ['collegeadmin', 'districtadmin', 'developer'],
                            LinkedStates: ['/manageterms'],
                            ActionRoute: ['/manageterms'],
                        },
                        {
                            MenuText: 'Manage Test Cutoffs',
                            MenuIcon: 'score',
                            AllowedRoles: ['districtadmin', 'developer'],
                            LinkedStates: ['/managescores'],
                            ActionRoute: ['/managescores'],
                        },
                        {
                            MenuText: 'Manage Courses',
                            MenuIcon: 'school',
                            AllowedRoles: ['districtadmin', 'developer'],
                            LinkedStates: ['/managecourses'],
                            ActionRoute: ['/managecourses'],
                        },
                        {
                            MenuText: 'Student Search',
                            MenuIcon: 'search',
                            AllowedRoles: ['counselor', 'schooladmin', 'districtadmin', 'developer'],
                            LinkedStates: ['/studentsearch'],
                            ActionRoute: ['/studentsearch'],
                        },
                        {
                            MenuText: 'Student Search',
                            MenuIcon: 'search',
                            AllowedRoles: ['advisor', 'collegeadmin', 'collegesupport'],
                            LinkedStates: ['/collegestudentsearch'],
                            ActionRoute: ['/collegestudentsearch'],
                        },
                        {
                            MenuText: 'Reports',
                            MenuIcon: 'assessment',
                            AllowedRoles: ['counselor', 'schooladmin', 'districtadmin', 'advisor', 'collegeadmin', 'collegesupport', 'developer'],
                            LinkedStates: ['/reports'],
                            ActionRoute: ['/reports'],
                        },
                        {
                            MenuText: 'Issue Alerts',
                            MenuIcon: 'notification_important',
                            AllowedRoles: ['developer'],
                            LinkedStates: ['/issuealert'],
                            ActionRoute: ['/issuealert'],
                        },
                        // NOTE: Temporarily diabled this route until tool can be linked to IIQ
                        // {
                        //     MenuText: 'File Issue Report',
                        //     MenuIcon: 'bug_report',
                        //     AllowedRoles: ['all'],
                        //     LinkedStates: ['/issuereport'],
                        //     ActionRoute: ['/issuereport'],
                        //     Exclude: this.User.CurrentRole === 'Developer' || this.User.CurrentRole === 'DistrictAdmin',
                        // },
                        {
                            MenuText: 'Issue Report Tracker',
                            MenuIcon: 'bug_report',
                            AllowedRoles: ['districtadmin', 'developer'],
                            LinkedStates: ['/issuereporttracker'],
                            ActionRoute: ['/issuereporttracker'],
                        },
                        { MenuText: 'Logout', MenuIcon: 'exit_to_app', AllowedRoles: ['all'], IsLogout: true },
                        { MenuText: 'About', MenuIcon: 'help', AllowedRoles: ['all'], IsAbout: true },
                    ];

                    this.MenuItems = tempmenu;
                }
            });

        // Subscribe to system online status updates. If the system goes offline, this may cause us to reroute to the offline route.
        self.authService.SystemOnlineSubject.pipe(
            takeUntil(self._subRelease),
            distinctUntilChanged((p, c) => p.Status === c.Status)
        ).subscribe((sysstatus) => {
            if (sysstatus && sysstatus.Status) {
                // Load/refresh the system variables needed for working the app.
                combineLatest(
                    this.appService.GetAllColleges(),
                    this.appService.GetCurrentSchoolYear(),
                    this.appService.GetAllSchoolYears(),
                    this.appService.DevSiteSubject,
                    this.authService.TestConnection(),
                    function (colleges, curYear, allYears, devSite, testConn) {
                        return {
                            Colleges: colleges,
                            CurrentYear: curYear,
                            AllYears: allYears,
                            DevSite: devSite,
                            TestConn: testConn,
                        };
                    }
                )
                    .pipe(takeUntil(this._subRelease))
                    .subscribe(
                        (status) => {
                            this.AvailableColleges = status.Colleges;
                            this.AllSchoolYears = status.AllYears;
                            this.DevSite = status.DevSite;
                            this.SelectedSchoolYear = this.appService.SelectedSchoolYear;

                            if (status.TestConn.DeltaVersion !== this.VersionLoaded) {
                                this.UpdateAvailable = true;
                            }

                            this.appService.IssueAlerts = status.TestConn && status.TestConn.Alerts;

                            this.ViewReady = true;

                            // Route to the loading of the login panel controls if the route is currently unrouted or offline.
                            if (!this.router.url || this.router.url === '/' || this.router.url === '/offline') {
                                // Go to the login route.
                                this.router.navigate(['/login']);
                                return;
                            }

                            // If we're trying to route to the login route, but we have a user object loaded, go to the home page instead.
                            if (this.router.url.indexOf('login') >= 0 && this.User && !!this.User.AuthToken) {
                                this.router.navigate(['/home']);
                                return;
                            }
                        },
                        (error) => {
                            this.errorService.HandleServiceError(error);
                        }
                    );
            } else {
                // The system is showing as offline. If there is no user authtoken active, route to the offline route.
                self.ViewReady = true;

                if (!self.userService || !self.userService.User || !self.userService.User.AuthToken) {
                    self.router.navigate([`/offline`]);
                }
            }
        });

        // Conduct an initial test of the connection, to set the proper status.
        this.authService.TestConnection().subscribe(
            (result) => {
                this.authService.SetSystemOnlineStatus(result);
            },
            (err) => {
                // A failure would indicate the system is offline.
                this.authService.SetSystemOnlineStatus({
                    Status: false,
                    SystemMessage: '',
                    DeltaVersion: VERSION_NUMBER,
                });
            }
        );
    }

    ngOnDestroy() {
        this._subRelease.next();
        this._subRelease.complete();
    }

    ngOnInit() {}

    // #region About Method

    About() {
        const self = this;

        if (self.SmallSize) {
            self.MenuOpen = false;
        }

        let msg =
            `The Dual Enrollment Link to Advisors (DELTA) System is a product of the Polk County Public ` +
            `Schools system and provides a single-point portal to allow students, school counselors, ` +
            `school-administrators, and college advisors to work together to ensure a smooth Dual Enrollment process for ` +
            `all district students who elect to participate in the Dual Enrollment Program.<br /><br />` +
            `<strong>DELTA Version: </strong>${self.VersionLoaded}<br /><strong>Build Date/Time: </strong>${self.BuildTime}`;

        if (self.BuildReason) {
            msg += `<br /><strong>Build Notes: </strong><br />` + self.BuildReason;
        }

        return self.dialogService.OpenMessageBoxDialog({
            TitleText: 'About DELTA System',
            MessageText: msg,
            height: self.BuildReason ? '450px' : '400px',
        });
    }

    // #endregion

    // #region Get Update Method

    GetUpdate() {
        // Get a confirmation
        this.dialogService
            .OpenConfirmationDialog({
                TitleText: 'Site Update Available',
                MessageText:
                    `An update is available to the DELTA System. Updates typically fix issues reported by ` +
                    `users or offer new features. If you are encountering problems with the site, it is recommended that ` +
                    `you load the update for the website at this time.<br /><br /><strong>Note: </strong>Loading the update ` +
                    `will refresh your page and you will lose any unsubmitted changes.`,
                OKText: 'Load Update',
                CancelText: 'CANCEL',
            })
            .subscribe((res) => {
                if (res === true) {
                    // User is opting to load the update. Refresh the page without caching.
                    location.reload(true);
                }
            });
    }

    // #endregion

    // #region Go Home Method

    GoHome() {
        if (this.userService.User == null || this.userService.User.AuthToken == null) {
            this.router.navigate(['/login']);
        } else {
            this.router.navigate(['/home']);
        }
    }

    // #endregion

    // #region Logout Method

    Logout() {
        // Pop up a confirmation box to confirm desire to logout.
        const opts: ConfirmationDialogOptions = {
            TitleText: 'Confirmation Requested',
            MessageText: 'You are about to log out of the DELTA System. Are you sure you wish to exit at this time?',
            OKText: 'Yes - Log Out',
            CancelText: 'No - Stay Logged In',
        };

        return this.dialogService.OpenConfirmationDialog(opts).pipe(
            tap((result) => {
                if (result === true) {
                    // User is opting to log out. Proceed.
                    this.authService.Logout(null, null);
                }
            })
        );
    }

    // #endregion

    // #region Navigate Method

    Navigate(event) {
        if (this.SmallSize) {
            this.MenuOpen = false;
        }
    }

    // #endregion

    // #region Toolbar About Method

    ToolbarAbout() {
        this.About().pipe(takeUntil(this._subRelease)).subscribe();
    }

    // #endregion

    // #region Toolbar Logout Method

    ToolbarLogout() {
        this.Logout().pipe(takeUntil(this._subRelease)).subscribe();
    }

    // #endregion

    // #region Update Admin College Method

    UpdateAdminCollege(event: MatSelectChange) {
        if (!event || event.value == null) {
            return;
        }

        // Set the application wide value
        this.appService.SelectedAdminCollegeID = event.value;
    }

    // #endregion

    // #region Toggle Nav Menu Method

    ToggleNavMenu() {
        this.MenuOpen = !this.MenuOpen;
    }

    // #endregion

    // #region Update Selected Year Method

    UpdateSelectedYear(event: MatSelectChange) {
        if (!event || !event.value) {
            return;
        }

        const newyear = <string>event.value;

        // Have the app service handle this update, which will then trigger to all subscribers
        this.appService.SelectedSchoolYear = newyear;

        // If we are currently in the record view when this happens, route to the record view with only the school year as
        // the parameter to ensure we get a record from that year.
        const url = this.router.url;

        if (url.indexOf('studentview') >= 0) {
            // Reroute
            this.router.navigate(['studentviewbyyear', this.appService.SelectedStudent.StudentID, newyear]);
        }
    }

    // #endregion
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts